import React, { useState, useEffect, useRef, useCallback } from "react";
import "./CSS/VideoUpload.css";
import ChooseIcon from "../../../Assets/Choose.svg";
import transcriptionIcon from "../../../Assets/transcription.svg";
import * as tus from "tus-js-client";
import {
  lightHaptic,
  mediumHaptic,
  notifyLocal,
} from "../../../native/capacitor";

const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const VideoUpload = ({
  onAppReload,
  onVideoTimeUpdate,
  onVideoMetadata,
  setNewVideoUploaded,
  setVideoId,
  videoId,
  videoDuration,
  seekToTime,
  handleTranscription,
  profile,
  video,
  setVideo,
  setIsDemo,
}) => {
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDemoActive, setIsDemoActive] = useState(false);
  const [isTranscriptionActive, setIsTranscriptionActive] = useState(false);
  const [isDemoDisabled, setIsDemoDisabled] = useState(false);
  const isUploadingRef = useRef(false);
  const activeUploadRef = useRef(null);

  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    await lightHaptic();

    const hadPreviousVideo = !!videoId;

    window._newUploadFile = file;

    if (hadPreviousVideo && onAppReload) {
      onAppReload();
    } else {
      proceedWithUpload(file);
    }
  };

  const handlePlayDemoVideo = async () => {
    if (isDemoDisabled) return;
    await lightHaptic();

    const hadPreviousVideo = !!videoId;

    window._startDemoAfterReset = true;

    if (hadPreviousVideo && onAppReload) {
      onAppReload();
    } else {
      startDemoVideo();
    }
  };

  const startDemoVideo = useCallback(async () => {
    if (isDemoDisabled) return;
    setIsDemoActive((prev) => !prev);
    setIsDemoDisabled(true);
    try {
      setVideo(process.env.PUBLIC_URL + "/demo/demo.MOV");
      const userId = profile ? profile.id : null;
      const response = await fetch(`${REACT_APP_API_BASE_URL}/api/start-demo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
        }),
      });
      const data = await response.json();
      setIsDemo(true);
      if (data.videoId) {
        setVideoId(data.videoId);
        setNewVideoUploaded(true);
      }
      await notifyLocal("Demo started", "Demo session is ready for review.");
      console.log("Demo video analysis started successfully.");
    } catch (error) {
      console.error("Error loading demo video:", error);
    }
  }, [
    isDemoDisabled,
    profile,
    setVideo,
    setVideoId,
    setNewVideoUploaded,
    setIsDemoActive,
    setIsDemoDisabled,
    setIsDemo,
  ]);

  const proceedWithUpload = useCallback(
    async (file) => {
      if (!file) return;
      const userId = profile ? profile.id : null;
      const videoId = crypto.randomUUID();

      const previewURL = URL.createObjectURL(file);
      setVideo(previewURL);
      setIsUploading(true);
      setUploadProgress(0);
      setIsDemo(false);

      const upload = new tus.Upload(file, {
      endpoint : `${REACT_APP_API_BASE_URL}/upload`,
      retryDelays : [0, 3000, 5000, 10000],
      storeFingerprintForResuming: true,
      chunkSize  : 8 * 1024 * 1024,
      metadata   : {
        filename : file.name,
        filetype : file.type,
        videoId,
        userId,
      },

      onProgress (bytesSent, bytesTotal) {
        setUploadProgress(Math.round((bytesSent / bytesTotal) * 100));
      },

      onSuccess () {
        console.log("✅ tus upload finished");
        isUploadingRef.current = false;
        setIsUploading(false);
        setNewVideoUploaded(true);
        setVideoId(videoId);
        notifyLocal("Upload complete", "Your training video is uploaded.");
        mediumHaptic();
      },

      onError (err) {
        console.error("tus upload failed →", err);
        isUploadingRef.current = false;
        setIsUploading(false);
        notifyLocal("Upload failed", "Please try uploading again.");
      },
    });
    activeUploadRef.current = upload;
    isUploadingRef.current = true;
    upload.start();
  },
    [profile, setVideoId, setNewVideoUploaded, setVideo, setIsDemo]
  );

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      const duration = videoRef.current.duration;
      onVideoTimeUpdate(currentTime, duration);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      const duration = videoRef.current.duration;
      onVideoTimeUpdate(0, duration);
      if (onVideoMetadata) {
        onVideoMetadata({
          width: videoRef.current.videoWidth || 0,
          height: videoRef.current.videoHeight || 0,
          duration: duration || 0,
        });
      }
    }
  };

  useEffect(() => {
    if (video && videoRef.current) {
      videoRef.current.src = video;
      videoRef.current.load();
    }
  }, [video]);

  const handleChooseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
    setIsDemoActive(false);
    lightHaptic();
  };

  useEffect(() => {
    if (window._newUploadFile) {
      const file = window._newUploadFile;
      delete window._newUploadFile;
      proceedWithUpload(file);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (window._startDemoAfterReset) {
      delete window._startDemoAfterReset;
      startDemoVideo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onOnline = () => {
      const upload = activeUploadRef.current;
      if (!isUploadingRef.current && upload && upload.url) {
        isUploadingRef.current = true;
        setIsUploading(true);
        upload.start();
      }
    };

    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);

  return (
    <div className={`training-video-container ${video ? "" : "empty"}`}>
      <div className="training-video-header">
        <div className="title-with-dot">
          <span className="orange-dot" />
          <h2>Training Video</h2>
        </div>
        <div className="training-video-buttons">
          <button className="choose-button" onClick={handleChooseClick}>
            <img
              src={ChooseIcon}
              alt="Choose Icon"
              className="choose-button-icon"
            />
            Upload Video
          </button>
          <button
            className={`demo-button ${isDemoActive ? "active" : ""}`}
            disabled={isDemoDisabled}
            onClick={(e) => {
              handlePlayDemoVideo();
            }}
          >
            <img
              src={ChooseIcon}
              alt="Choose Icon"
              className="choose-button-icon"
            />
            Demo Video
          </button>
          <button
            className={`transcription-button ${
              isTranscriptionActive ? "active" : ""
            }`}
            onClick={async () => {
              await lightHaptic();
              handleTranscription();
              setIsTranscriptionActive((prev) => !prev);
            }}
          >
            <img
              src={transcriptionIcon}
              alt="Transcription Icon"
              className="choose-button-icon"
            />
            Transcription
          </button>
        </div>
      </div>

      <div className="orange-divider" />

      <div className={`video-area-container ${video ? "" : "empty"}`}>
          <div
            className={`video-area ${video ? "active" : "empty"}`}
            onClick={() => {
              if (!video) {
                handleChooseClick();
              }
            }}
          >
            {!video && (
              <div className="video-placeholder">Choose Video</div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              onChange={handleVideoUpload}
              style={{ display: "none" }}
            />
            {video && (
              <video
                key={video}
                ref={videoRef}
                playsInline
                width="100%"
                controls
                onLoadedMetadata={handleLoadedMetadata}
                onTimeUpdate={handleTimeUpdate}
                src={video}
              >
                Your browser does not support the video tag.
              </video>
            )}
            {isUploading && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <span className="progress-text">
                    Uploading Video... {uploadProgress}%
                  </span>
                  <div
                    className="progress-fill"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
      </div>
    </div>
  );
};

export default VideoUpload;
